import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { useAuth } from './context/AuthContext';
import Layout from './components/Layout/Layout';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PatientRegistrationPage from './pages/PatientRegistrationPage';
import DashboardPage from './pages/DashboardPage';
import PatientsPage from './pages/PatientsPage';
import LeadsPage from './pages/LeadsPage';
import { LeadStatusConfigPage } from './pages/LeadStatusConfigPage';
import { LeadFieldConfigPage } from './pages/LeadFieldConfigPage';
import VideoRoomsPage from './pages/VideoRoomsPage';
import PatientDetailPage from './pages/PatientDetailPage';
import MeetingsPage from './pages/MeetingsPage';
import MeetingDetailPage from './pages/MeetingDetailPage';
import CalendarPage from './pages/CalendarPage';
import UsersPage from './pages/UsersPage';
import TenantsPage from './pages/TenantsPage';
import ClinicsPage from './pages/ClinicsPage';
import MeetingTypesPage from './pages/MeetingTypesPage';
import SummaryTypesPage from './pages/SummaryTypesPage';
import MedicationsPage from './pages/MedicationsPage';
import { FileTypesPage } from './pages/FileTypesPage';
import { PatientStatusConfigPage } from './pages/PatientStatusConfigPage';
import { PatientTypesPage } from './pages/PatientTypesPage';
import ServicesPage from './pages/ServicesPage';
import { PatientFieldConfigPage } from './pages/PatientFieldConfigPage';
import ProfilePage from './pages/ProfilePage';
import ActivityLogsPage from './pages/ActivityLogsPage';
import UnmatchedCommunicationsPage from './pages/UnmatchedCommunicationsPage';
import PromptManagementPage from './pages/PromptManagementPage';
import SmsTemplatesPage from './pages/SmsTemplatesPage';
import EmailTemplatesPage from './pages/EmailTemplatesPage';
import UserPrivilegesPage from './pages/UserPrivilegesPage';
import { PublicQuestionnaireWizardPage } from './pages/PublicQuestionnaireWizardPage';
import { QuestionnaireExitedPage } from './pages/QuestionnaireExitedPage';
import { QuestionnaireBuilderPage } from './components/questionnaires/QuestionnaireBuilderPage';
import QuestionnairesPage from './pages/QuestionnairesPage';
import PatientSchedulePage from './pages/PatientSchedulePage';
import UpdateMeetingPage from './pages/UpdateMeetingPage';
import PatientNotificationsPage from './pages/PatientNotificationsPage';
import TaskRulesPage from './pages/TaskRulesPage';
import WellnessConfigPage from './pages/WellnessConfigPage';
import WellnessItemPage from './pages/WellnessItemPage';
import WellnessTipPage from './pages/WellnessTipPage';
import DiagnosisTypesPage from './pages/DiagnosisTypesPage';
import TasksPage from './pages/TasksPage';
import InternalTasksPage from './pages/InternalTasksPage';
import InternalTaskTypesPage from './pages/InternalTaskTypesPage';
import ConfigSyncPage from './pages/ConfigSyncPage';
import DocAssistantPage from './pages/DocAssistantPage';
import ReferralsPage from './pages/ReferralsPage';
import LoadingSpinner from './components/common/LoadingSpinner';
import { VideoCallProvider } from './contexts/VideoCallContext';
import { CallProvider } from './contexts/CallContext';

// Lazy: the LiveKit client is ~150 kB gzip and must not sit in the main bundle.
// Nothing is fetched until a therapist actually opens a call.
const FloatingVideoWindow = lazy(() => import('./components/video/FloatingVideoWindow'));
// Lazy for the same reason: the Twilio Voice SDK is only needed once a therapist
// actually places a WhatsApp call.
const CallWidget = lazy(() => import('./components/CallWidget'));

//Main application component with routing and layout tr123
function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <LoadingSpinner />
      </Box>
    );
  }

  // Return routes with public registration always accessible
  return (
    <VideoCallProvider>
    <CallProvider>
      {/* Above <Routes> on purpose: the call must survive navigation so the
          therapist can work through the session (patient page, summary) without
          dropping the room. */}
      <Suspense fallback={null}>
        <FloatingVideoWindow />
        <CallWidget />
      </Suspense>
    <Routes>
      {/* Public routes (no authentication required) */}
      <Route path="/register/:clinicId" element={<PatientRegistrationPage />} />
      <Route path="/schedule/:patientUuid" element={<PatientSchedulePage />} />
      <Route path="/update-meeting/:uuid" element={<UpdateMeetingPage />} />
      <Route path="/questionnaire/:token" element={<PublicQuestionnaireWizardPage />} />
      <Route path="/questionnaire/:tenantId/:questionnairePath" element={<PublicQuestionnaireWizardPage />} />
      <Route path="/questionnaire/exited" element={<QuestionnaireExitedPage />} />

      {!user ? (
        <>
          {/* Unauthenticated routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </>
      ) : (
        <>
          {/* Authenticated routes with Layout */}
          <Route path="/" element={<Navigate to="/calendar" replace />} />
          <Route path="/dashboard" element={<Layout><DashboardPage /></Layout>} />
          <Route path="/patients" element={<Layout><PatientsPage /></Layout>} />
          <Route path="/leads" element={<Layout><LeadsPage /></Layout>} />
          <Route path="/patients/:id" element={<Layout><PatientDetailPage /></Layout>} />
          <Route path="/meetings" element={<Layout><MeetingsPage /></Layout>} />
          <Route path="/meetings/:id" element={<Layout><MeetingDetailPage /></Layout>} />
          <Route path="/tasks" element={<Layout><TasksPage /></Layout>} />
          <Route path="/internal-tasks" element={<Layout><InternalTasksPage /></Layout>} />
          <Route path="/config-sync" element={<Layout><ConfigSyncPage /></Layout>} />
          <Route path="/calendar" element={<Layout><CalendarPage /></Layout>} />
          <Route path="/profile" element={<Layout><ProfilePage /></Layout>} />

          {/* Tenant-specific routes (all authenticated users) */}
          <Route path="/questionnaires" element={<Layout><QuestionnairesPage /></Layout>} />
          <Route path="/sms-templates" element={<Layout><SmsTemplatesPage /></Layout>} />
          <Route path="/email-templates" element={<Layout><EmailTemplatesPage /></Layout>} />

          {/* Open video rooms: same privilege that shows the sidebar entry, so the
              page cannot be reached by typing the URL without it. */}
          {user.user_privilege?.view_video_rooms && (
            <Route path="/video-rooms" element={<Layout><VideoRoomsPage /></Layout>} />
          )}

          {/* Manage-users privilege */}
          {user.user_privilege?.manage_users && (
            <>
              <Route path="/users" element={<Layout><UsersPage /></Layout>} />
              <Route path="/user-privileges" element={<Layout><UserPrivilegesPage /></Layout>} />
            </>
          )}

          {/* Superadmin only (tenant 0) */}
          {user.tenant_id === 0 && (
            <Route path="/tenants" element={<Layout><TenantsPage /></Layout>} />
          )}

          {/* Questionnaires privilege */}
          {user.user_privilege?.questionnaires && (
            <Route path="/questionnaire-types" element={<Layout><QuestionnaireBuilderPage /></Layout>} />
          )}

          {/* Config privilege */}
          {user.user_privilege?.config && (
            <>
              <Route path="/clinics" element={<Layout><ClinicsPage /></Layout>} />
              <Route path="/meeting-types" element={<Layout><MeetingTypesPage /></Layout>} />
              <Route path="/summary-types" element={<Layout><SummaryTypesPage /></Layout>} />
              <Route path="/medications" element={<Layout><MedicationsPage /></Layout>} />
              <Route path="/patient-status-configs" element={<Layout><PatientStatusConfigPage /></Layout>} />
              <Route path="/patient-types" element={<Layout><PatientTypesPage /></Layout>} />
              <Route path="/services" element={<Layout><ServicesPage /></Layout>} />
              <Route path="/patient-field-configs" element={<Layout><PatientFieldConfigPage /></Layout>} />
              <Route path="/lead-status-configs" element={<Layout><LeadStatusConfigPage /></Layout>} />
              <Route path="/lead-field-configs" element={<Layout><LeadFieldConfigPage /></Layout>} />
              <Route path="/activity-logs" element={<Layout><ActivityLogsPage /></Layout>} />
              <Route path="/unmatched-communications" element={<Layout><UnmatchedCommunicationsPage /></Layout>} />
              <Route path="/patient-notifications" element={<Layout><PatientNotificationsPage /></Layout>} />
              <Route path="/task-rules" element={<Layout><TaskRulesPage /></Layout>} />
            </>
          )}

          {/* File-types privilege */}
          {user.user_privilege?.file_types && (
            <Route path="/file-types" element={<Layout><FileTypesPage /></Layout>} />
          )}

          {/* Task-types privilege */}
          {user.user_privilege?.task_types && (
            <Route path="/internal-task-types" element={<Layout><InternalTaskTypesPage /></Layout>} />
          )}

          {/* Referral privilege */}
          {user.user_privilege?.referral && (
            <Route path="/referrals" element={<Layout><ReferralsPage /></Layout>} />
          )}

          {/* Doc assistant privilege */}
          {user.user_privilege?.doc_assistant && (
            <Route path="/doc-assistant" element={<Layout><DocAssistantPage /></Layout>} />
          )}

          {/* Prompt-development privilege */}
          {user.user_privilege?.prompt_development && (
            <Route path="/prompts" element={<Layout><PromptManagementPage /></Layout>} />
          )}

          {user.user_privilege?.wellness && (
            <>
              <Route path="/wellness-configs" element={<Layout><WellnessConfigPage /></Layout>} />
              <Route path="/wellness-items" element={<Layout><WellnessItemPage /></Layout>} />
              <Route path="/wellness-tips" element={<Layout><WellnessTipPage /></Layout>} />
              <Route path="/diagnosis-types" element={<Layout><DiagnosisTypesPage /></Layout>} />
            </>
          )}

          <Route path="*" element={<Navigate to="/calendar" replace />} />
        </>
      )}
    </Routes>
    </CallProvider>
    </VideoCallProvider>
  );
}

export default App;